import { useState } from "react";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  Target, Users, CheckCircle2, Clock, XCircle, MapPin,
  Briefcase, Send, ArrowRight, AlertCircle, Phone, Mail,
  Building2, Zap, BarChart3, TrendingUp,
} from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

const Card = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div className={`p-5 rounded-lg bg-card border border-border shadow-card ${className}`}>{children}</div>
);

const SERVICE_TYPES = [
  "GST Filing", "Tax Planning", "Audit & Assurance", "Company Registration",
  "Bookkeeping", "Payroll Management", "TDS Returns", "ITR Filing",
  "Business Advisory", "Financial Planning",
];

const CITIES = [
  "Mumbai", "Delhi", "Bangalore", "Chennai", "Hyderabad", "Pune",
  "Kolkata", "Ahmedabad", "Jaipur", "Lucknow", "Surat", "Indore",
];

const statusCfg: Record<string, { label: string; color: string; icon: typeof Clock }> = {
  unassigned: { label: "Unassigned", color: "bg-muted/20 text-muted-foreground border-border", icon: Clock },
  assigned: { label: "Assigned", color: "bg-accent/10 text-accent border-accent/20", icon: Target },
  accepted: { label: "Accepted", color: "bg-success/10 text-success border-success/20", icon: CheckCircle2 },
  declined: { label: "Declined", color: "bg-destructive/10 text-destructive border-destructive/20", icon: XCircle },
  completed: { label: "Completed", color: "bg-primary/10 text-primary border-primary/20", icon: CheckCircle2 },
};

export default function LeadGenerationPage() {
  const { user, role } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isCA = role === "ca";

  const [form, setForm] = useState({
    business_name: "", contact_name: "", contact_email: "",
    contact_phone: "", city: "", service_needed: "",
    description: "", budget: "", urgency: "normal",
  });

  // Fetch leads
  const { data: leads = [], isLoading } = useQuery({
    queryKey: ["partner-leads"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("partner_leads")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  // Submit lead
  const submitLead = useMutation({
    mutationFn: async () => {
      // Auto-assign: find a CA in the same city
      const { data: matchingCAs } = await supabase
        .from("ca_profiles")
        .select("user_id, city")
        .ilike("city", form.city)
        .eq("is_verified", true)
        .limit(1);

      const assignedCA = matchingCAs?.[0]?.user_id || null;

      const { error } = await supabase.from("partner_leads").insert({
        business_name: form.business_name,
        contact_name: form.contact_name,
        contact_email: form.contact_email,
        contact_phone: form.contact_phone || null,
        city: form.city,
        service_needed: form.service_needed,
        description: form.description || null,
        budget: form.budget || null,
        urgency: form.urgency,
        status: assignedCA ? "assigned" : "unassigned",
        assigned_ca_user_id: assignedCA,
        assigned_at: assignedCA ? new Date().toISOString() : null,
        requester_user_id: user?.id || null,
      });
      if (error) throw error;
      return { assigned: !!assignedCA };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["partner-leads"] });
      toast({
        title: "Lead submitted!",
        description: result.assigned
          ? "A nearby CA partner has been automatically assigned."
          : "We'll match you with a CA partner soon.",
      });
      setForm({
        business_name: "", contact_name: "", contact_email: "",
        contact_phone: "", city: "", service_needed: "",
        description: "", budget: "", urgency: "normal",
      });
    },
    onError: () => toast({ title: "Error", description: "Failed to submit lead.", variant: "destructive" }),
  });

  // CA accept/decline lead
  const respondLead = useMutation({
    mutationFn: async ({ id, action }: { id: string; action: "accepted" | "declined" }) => {
      const { error } = await supabase
        .from("partner_leads")
        .update({ status: action, responded_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_, { action }) => {
      queryClient.invalidateQueries({ queryKey: ["partner-leads"] });
      toast({ title: action === "accepted" ? "Lead accepted!" : "Lead declined" });
    },
  });

  // Stats
  const stats = {
    total: leads.length,
    unassigned: leads.filter((l) => l.status === "unassigned").length,
    assigned: leads.filter((l) => l.status === "assigned").length,
    accepted: leads.filter((l) => l.status === "accepted").length,
    declined: leads.filter((l) => l.status === "declined").length,
  };

  const myLeads = isCA
    ? leads.filter((l) => l.assigned_ca_user_id === user?.id)
    : leads.filter((l) => l.requester_user_id === user?.id);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold font-display text-foreground">Lead Generation</h1>
        <p className="text-sm text-muted-foreground">
          {isCA ? "View and respond to business leads assigned to you" : "Request accounting help — we'll match you with a nearby CA partner"}
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: "Total Leads", value: stats.total, icon: BarChart3, color: "text-accent" },
          { label: "Unassigned", value: stats.unassigned, icon: Clock, color: "text-muted-foreground" },
          { label: "Assigned", value: stats.assigned, icon: Target, color: "text-accent" },
          { label: "Accepted", value: stats.accepted, icon: TrendingUp, color: "text-success" },
          { label: "Declined", value: stats.declined, icon: XCircle, color: "text-destructive" },
        ].map((s, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
            <Card>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] text-muted-foreground">{s.label}</span>
                <s.icon className={`h-4 w-4 ${s.color}`} />
              </div>
              <div className="text-xl font-bold font-display text-foreground">{s.value}</div>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Lead Request Form — for non-CA roles */}
      {!isCA && (
        <Card>
          <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
            <Send className="h-4 w-4 text-accent" /> Request Accounting Help
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Business Name *</label>
              <div className="relative">
                <Building2 className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input className="pl-9" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} placeholder="Your company name" />
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Contact Name *</label>
              <div className="relative">
                <Users className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input className="pl-9" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder="Full name" />
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Email *</label>
              <div className="relative">
                <Mail className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input className="pl-9" type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} placeholder="email@example.com" />
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Phone</label>
              <div className="relative">
                <Phone className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input className="pl-9" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} placeholder="+91 98765 43210" />
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">City *</label>
              <Select value={form.city} onValueChange={(v) => setForm({ ...form, city: v })}>
                <SelectTrigger><SelectValue placeholder="Select city" /></SelectTrigger>
                <SelectContent>
                  {CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Service Needed *</label>
              <Select value={form.service_needed} onValueChange={(v) => setForm({ ...form, service_needed: v })}>
                <SelectTrigger><SelectValue placeholder="Select service" /></SelectTrigger>
                <SelectContent>
                  {SERVICE_TYPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Budget</label>
              <Input value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} placeholder="₹5,000 - ₹10,000" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Urgency</label>
              <Select value={form.urgency} onValueChange={(v) => setForm({ ...form, urgency: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High — Need ASAP</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs text-muted-foreground mb-1 block">Description</label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Describe what you need help with..." rows={3} />
            </div>
          </div>
          <Button
            className="mt-4"
            disabled={!form.business_name || !form.contact_name || !form.contact_email || !form.city || !form.service_needed || submitLead.isPending}
            onClick={() => submitLead.mutate()}
          >
            <Zap className="h-4 w-4 mr-2" />
            {submitLead.isPending ? "Submitting..." : "Submit & Auto-Match CA"}
          </Button>
        </Card>
      )}

      {/* Leads List */}
      <Card>
        <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
          <Briefcase className="h-4 w-4 text-accent" />
          {isCA ? `Your Assigned Leads (${myLeads.length})` : `Your Leads (${myLeads.length})`}
        </h3>
        {isLoading ? (
          <p className="text-sm text-muted-foreground py-6 text-center">Loading leads...</p>
        ) : myLeads.length === 0 ? (
          <div className="text-center py-10">
            <AlertCircle className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">
              {isCA ? "No leads assigned to you yet." : "You haven't submitted any leads yet."}
            </p>
          </div>
        ) : (
          <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
            {myLeads.map((lead) => {
              const cfg = statusCfg[lead.status] || statusCfg.unassigned;
              const StatusIcon = cfg.icon;
              return (
                <motion.div
                  key={lead.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex items-start justify-between p-4 rounded-lg bg-muted/20 border border-border/50 gap-3"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-foreground">{lead.business_name}</p>
                      <Badge variant="outline" className={`text-[10px] ${cfg.color} flex items-center gap-1`}>
                        <StatusIcon className="h-2.5 w-2.5" /> {cfg.label}
                      </Badge>
                      {lead.urgency === "high" && (
                        <Badge variant="outline" className="text-[10px] bg-destructive/10 text-destructive border-destructive/20">
                          Urgent
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {lead.contact_name} · {lead.contact_email}
                      {lead.contact_phone && ` · ${lead.contact_phone}`}
                    </p>
                    <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {lead.city}</span>
                      <span className="flex items-center gap-1"><Briefcase className="h-3 w-3" /> {lead.service_needed}</span>
                      {lead.budget && <span>Budget: {lead.budget}</span>}
                    </div>
                    {lead.description && (
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{lead.description}</p>
                    )}
                  </div>

                  {/* CA actions */}
                  {isCA && lead.status === "assigned" && (
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs border-success/30 text-success hover:bg-success/10"
                        onClick={() => respondLead.mutate({ id: lead.id, action: "accepted" })}
                      >
                        <CheckCircle2 className="h-3 w-3 mr-1" /> Accept
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs border-destructive/30 text-destructive hover:bg-destructive/10"
                        onClick={() => respondLead.mutate({ id: lead.id, action: "declined" })}
                      >
                        <XCircle className="h-3 w-3 mr-1" /> Decline
                      </Button>
                    </div>
                  )}

                  {/* Status arrow for non-actionable */}
                  {(!isCA || lead.status !== "assigned") && (
                    <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-1" />
                  )}
                </motion.div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
